import { Icon } from '@dynamic/ui';
import {
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Button } from '#/components/Button/Button';
import { PickerItemBaseProps } from '#/components/Picker/PickerItem';
import { MarginlessMarkdown } from '#/pages/Settings/components/styled';
import { ButtonVariant } from '#/service/constants';
import { tb } from '#/store/translation/translation.store';

import {
  isProductPickerDivider,
  ProductPickerItem,
} from '../PlanPurchaseFlow/PickProductStep/getPickerItems';
import {
  ActionButton,
  Card,
  CardContent,
  CardWrapper,
  DescriptionMarkdown,
  DetailContainer,
  DetailText,
  Divider,
  PromotionalLabel,
  ShowMoreButton,
  SubtitleContainer,
  Title,
} from './ProductCard.styled';

interface DetailProps {
  detail?: string;
  renderDetail?: (detail: string) => ReactNode;
}

const Detail: FC<DetailProps> = ({ detail, renderDetail }) => {
  if (!detail) {
    return null;
  }

  return (
    <DetailContainer>
      {renderDetail ? renderDetail(detail) : <DetailText>{detail}</DetailText>}
    </DetailContainer>
  );
};

interface DescriptionProps {
  subtitle?: string;
  description?: string;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  renderSubtitle?: (subtitle: string) => ReactNode;
}

interface SubtitleProps {
  subtitle: string;
  renderSubtitle?: (subtitle: string) => ReactNode;
}

export const Subtitle: FC<SubtitleProps> = ({ subtitle, renderSubtitle }) => {
  // if a custom subtitle component is provided to render the subtitle, use it.
  if (renderSubtitle) {
    return renderSubtitle(subtitle);
  }

  // otherwise, render the subtitle as markdown.
  return <MarginlessMarkdown>{subtitle}</MarginlessMarkdown>;
};

const Description: FC<DescriptionProps> = ({
  subtitle = '',
  description = '',
  isExpanded,
  onToggleExpanded,
  renderSubtitle,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const container = containerRef.current;

    if (!container || isExpanded) {
      return;
    }

    if (description) {
      const paragraphCount = description.split('\n\n').length;
      setIsTruncated(paragraphCount > 3);
    } else {
      setIsTruncated(container.scrollHeight > container.clientHeight);
    }
  }, [isExpanded, description]);

  //Initial check for show more button
  useEffect(() => {
    checkTruncation();
  }, [subtitle, isExpanded, checkTruncation]);

  const showExpandButton = isTruncated || isExpanded;

  return (
    <>
      <SubtitleContainer
        $isExpanded={isExpanded}
        $showExpandButton={showExpandButton}
        ref={containerRef}
      >
        {description ? (
          <DescriptionMarkdown $isExpanded={isExpanded}>
            {description}
          </DescriptionMarkdown>
        ) : (
          <Subtitle renderSubtitle={renderSubtitle} subtitle={subtitle} />
        )}
      </SubtitleContainer>
      {showExpandButton && (
        <ShowMoreButton onClick={onToggleExpanded}>
          {isExpanded ? tb('programlist.showLess') : tb('programlist.showMore')}
          <Icon name={isExpanded ? 'chevron_up' : 'chevron_down'} />
        </ShowMoreButton>
      )}
    </>
  );
};

export interface ProductCardProps {
  autoFocus?: boolean;
  item: ProductPickerItem;
  onContinue: (id: string) => void;
  renderDetail?: (detail: string) => ReactNode;
  renderSubtitle?: (subtitle: string) => ReactNode;
  renderItem?: (
    item: PickerItemBaseProps,
    onContinue: (id: string) => void,
    autoFocus: boolean | undefined
  ) => ReactElement;
}

export const ProductCard: FC<ProductCardProps> = ({
  autoFocus,
  item,
  onContinue,
  renderDetail,
  renderSubtitle,
  renderItem,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (isProductPickerDivider(item)) {
    return null;
  }

  const { id, title, subtitle, detail, description, promotionalLabel } = item;

  const handleContinue = () => {
    onContinue(id);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (renderItem) {
    return renderItem(item, onContinue, autoFocus);
  }

  return (
    <CardWrapper data-testid={`product-card-${id}`}>
      {promotionalLabel && (
        <PromotionalLabel data-testid="product-card-promotional-label">
          {promotionalLabel}
        </PromotionalLabel>
      )}
      <Card $hasPromotionalLabel={Boolean(promotionalLabel)}>
        <CardContent>
          <Title>{title}</Title>
          <Detail detail={detail} renderDetail={renderDetail} />
          <Divider />
          <Description
            description={description}
            isExpanded={isExpanded}
            onToggleExpanded={toggleExpanded}
            renderSubtitle={renderSubtitle}
            subtitle={subtitle}
          />
          <ActionButton>
            <Button
              autoFocus={autoFocus}
              isFullWidth
              onClick={handleContinue}
              variant={ButtonVariant.Primary}
            >
              {tb('button.continue')}
            </Button>
          </ActionButton>
        </CardContent>
      </Card>
    </CardWrapper>
  );
};
